/**
 * Demand Terrain Explorer
 *
 * A three-level zoomable D3 visualization of demand clusters.
 * Level 1 (zoom < 3): Cluster bubbles sized by member_count, colored by dominant source.
 * Level 2 (zoom 3-12): Query dots within the active cluster.
 * Level 3 (zoom > 12): Query text labels at positions.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSite } from '@/contexts/SiteContext';
import { authFetch, handleResponse } from '@/utils/auth-fetch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import * as d3 from 'd3';
import {
  ChevronRight, Search, TrendingUp, MessageSquare, Globe2, HelpCircle,
  Layers, Eye, BarChart3, ArrowLeft
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────

const num = (v: unknown): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const fmt = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
};

const fmtEur = (n: number): string => `${fmt(n)}`;

// ── Source config ────────────────────────────────────────────────────────

const SOURCE_CONFIG: Record<string, { fill: string; stroke: string; label: string; icon: React.ElementType }> = {
  site_search: { fill: '#E6F1FB', stroke: '#378ADD', label: 'Site Search', icon: Search },
  onsite_search: { fill: '#E6F1FB', stroke: '#378ADD', label: 'Site Search', icon: Search },
  gsc: { fill: '#E1F5EE', stroke: '#1D9E75', label: 'GSC', icon: TrendingUp },
  chatbot: { fill: '#FAECE7', stroke: '#D85A30', label: 'Chatbot', icon: MessageSquare },
  paa: { fill: '#EEEDFE', stroke: '#534AB7', label: 'PAA', icon: HelpCircle },
  aeo_question: { fill: '#FEF3C7', stroke: '#D97706', label: 'AEO', icon: Globe2 },
  google_ads_live: { fill: '#FDE8E8', stroke: '#DC2626', label: 'Google Ads', icon: TrendingUp },
  google_ads: { fill: '#FDE8E8', stroke: '#DC2626', label: 'Google Ads', icon: TrendingUp },
  competitor_faq: { fill: '#F0FDF4', stroke: '#16A34A', label: 'Competitor FAQ', icon: HelpCircle },
  community: { fill: '#FDF4FF', stroke: '#A855F7', label: 'Community', icon: MessageSquare },
  ai_research: { fill: '#EFF6FF', stroke: '#3B82F6', label: 'AI Research', icon: Globe2 },
};

const DEFAULT_SOURCE = { fill: '#F3F4F6', stroke: '#9CA3AF', label: 'Other', icon: Layers };

function getSourceConfig(source: string) {
  return SOURCE_CONFIG[source] || DEFAULT_SOURCE;
}

// ── API ──────────────────────────────────────────────────────────────────

const API_BASE = '/api/v1/explorer';

interface TerrainCluster {
  id: string;
  label: string;
  member_count: number;
  total_commercial_value_eur: number;
  trending: boolean;
  depth: number;
  parent_cluster_id: string | null;
  gap_score: number;
  coverage_pct: number;
  umap_x: number;
  umap_y: number;
  source_mix: { source_type: string; count: number }[];
  dominant_source: string;
}

interface NeighbourhoodQuery {
  id: string;
  text: string;
  text_original: string;
  source_type: string;
  frequency: number;
  commercial_value: number;
  x: number;
  y: number;
  answered: boolean;
}

interface SubCluster {
  id: string;
  label: string;
  member_count: number;
  total_commercial_value_eur: number;
  umap_x: number;
  umap_y: number;
}

interface NeighbourhoodData {
  cluster: {
    id: string;
    label: string;
    member_count: number;
    total_commercial_value_eur: number;
    gap_score: number;
    coverage_pct: number;
  };
  sub_clusters: SubCluster[];
  queries: NeighbourhoodQuery[];
  source_breakdown: { source_type: string; count: number }[];
  total_queries: number;
}

interface QueryDetail {
  id: string;
  text_original: string;
  text_normalised: string;
  source_type: string;
  search_volume: number;
  commercial_value: number;
  market: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

async function fetchTerrain(siteId: string, market?: string): Promise<{ clusters: TerrainCluster[]; markets: string[] }> {
  const params = new URLSearchParams({ siteId });
  if (market) params.set('market', market);
  const resp = await authFetch(`${API_BASE}/terrain?${params}`);
  const data = await handleResponse<{ clusters: TerrainCluster[]; markets: string[] }>(resp);
  return data;
}

async function fetchNeighbourhood(clusterId: string, source?: string): Promise<NeighbourhoodData> {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  const resp = await authFetch(`${API_BASE}/clusters/${clusterId}/neighbourhood?${params}`);
  return handleResponse<NeighbourhoodData>(resp);
}

async function fetchQueryDetail(clusterId: string, search?: string): Promise<{ queries: QueryDetail[]; total: number }> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  const resp = await authFetch(`${API_BASE}/clusters/${clusterId}/queries?${params}`);
  return handleResponse<{ queries: QueryDetail[]; total: number }>(resp);
}

// ── Source filter pills ──────────────────────────────────────────────────

const ALL_SOURCES = ['site_search', 'gsc', 'chatbot', 'paa', 'aeo_question', 'google_ads_live'];

function SourceFilterPills({
  activeSource,
  onSelect,
}: {
  activeSource: string | null;
  onSelect: (s: string | null) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => onSelect(null)}
        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
          activeSource === null
            ? 'bg-foreground text-background'
            : 'bg-muted text-muted-foreground hover:bg-muted/80'
        }`}
      >
        All
      </button>
      {ALL_SOURCES.map(src => {
        const cfg = getSourceConfig(src);
        const active = activeSource === src;
        return (
          <button
            key={src}
            onClick={() => onSelect(active ? null : src)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
              active
                ? 'ring-2 ring-offset-1'
                : 'hover:opacity-80'
            }`}
            style={{
              backgroundColor: cfg.fill,
              color: cfg.stroke,
              borderColor: cfg.stroke,
              ...(active ? { ringColor: cfg.stroke } : {}),
            }}
          >
            <span
              className="w-2 h-2 rounded-full inline-block"
              style={{ backgroundColor: cfg.stroke }}
            />
            {cfg.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Breadcrumb ───────────────────────────────────────────────────────────

function Breadcrumb({
  items,
  onNavigate,
}: {
  items: { label: string; id: string | null }[];
  onNavigate: (index: number) => void;
}) {
  return (
    <div className="flex items-center gap-1 text-sm">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          <button
            onClick={() => onNavigate(i)}
            className={`px-1.5 py-0.5 rounded hover:bg-muted transition-colors truncate max-w-[200px] ${
              i === items.length - 1
                ? 'font-semibold text-foreground'
                : 'text-muted-foreground'
            }`}
          >
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Right panel components ───────────────────────────────────────────────

function CounterList({
  items,
}: {
  items: { label: string; count: number; color: string }[];
}) {
  const total = items.reduce((s, i) => s + i.count, 0);
  return (
    <div className="space-y-1.5">
      {items.map(item => (
        <div key={item.label} className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-muted-foreground">{item.label}</span>
          </div>
          <span className="font-medium tabular-nums">{fmt(item.count)}</span>
        </div>
      ))}
      {items.length > 0 && (
        <div className="flex items-center justify-between text-xs pt-1 border-t">
          <span className="font-medium">Total</span>
          <span className="font-semibold tabular-nums">{fmt(total)}</span>
        </div>
      )}
    </div>
  );
}

function QueryDetailCard({ query }: { query: NeighbourhoodQuery | QueryDetail | null }) {
  if (!query) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm">
        Click a query to see details
      </div>
    );
  }

  const cfg = getSourceConfig(query.source_type);
  const text = 'text_original' in query ? query.text_original : ('text' in query ? (query as NeighbourhoodQuery).text : '');
  const volume = 'search_volume' in query ? num((query as QueryDetail).search_volume) : num((query as NeighbourhoodQuery).frequency);
  const value = num(query.commercial_value);

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-lg border bg-card">
        <p className="text-sm font-medium leading-snug mb-2">{text}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant="outline"
            className="text-xs"
            style={{ borderColor: cfg.stroke, color: cfg.stroke, backgroundColor: cfg.fill }}
          >
            {cfg.label}
          </Badge>
          {'answered' in query && (
            <Badge variant={(query as NeighbourhoodQuery).answered ? 'default' : 'destructive'} className="text-xs">
              {(query as NeighbourhoodQuery).answered ? 'Answered' : 'Unanswered'}
            </Badge>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="p-2 rounded bg-muted/50">
          <div className="text-muted-foreground">Volume</div>
          <div className="font-semibold tabular-nums">{fmt(volume)}</div>
        </div>
        <div className="p-2 rounded bg-muted/50">
          <div className="text-muted-foreground">Value</div>
          <div className="font-semibold tabular-nums">{fmtEur(value)}</div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export default function TerrainExplorer() {
  const { selectedSite } = useSite();
  const siteId = selectedSite?.id;

  // State
  const [activeMarket, setActiveMarket] = useState<string | null>(null);
  const [activeClusterId, setActiveClusterId] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState<string | null>(null);
  const [selectedQuery, setSelectedQuery] = useState<NeighbourhoodQuery | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Refs
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // ── Data queries ─────────────────────────────────────────────────────

  const { data: terrainData, isLoading: terrainLoading } = useQuery({
    queryKey: ['explorer-terrain', siteId, activeMarket],
    queryFn: () => fetchTerrain(siteId!, activeMarket || undefined),
    enabled: !!siteId,
  });

  const clusters = terrainData?.clusters || [];
  const markets = terrainData?.markets || [];

  // Auto-select first market
  useEffect(() => {
    if (!activeMarket && markets.length > 0) {
      setActiveMarket(markets[0]);
    }
  }, [markets, activeMarket]);

  const { data: neighbourhoodData, isLoading: neighbourhoodLoading } = useQuery({
    queryKey: ['explorer-neighbourhood', activeClusterId, activeSource],
    queryFn: () => fetchNeighbourhood(activeClusterId!, activeSource || undefined),
    enabled: !!activeClusterId,
  });

  // ── Breadcrumb ───────────────────────────────────────────────────────

  const breadcrumbItems = useMemo(() => {
    const items: { label: string; id: string | null }[] = [
      { label: 'All clusters', id: null },
    ];
    if (activeClusterId && neighbourhoodData) {
      items.push({ label: neighbourhoodData.cluster.label, id: activeClusterId });
    }
    return items;
  }, [activeClusterId, neighbourhoodData]);

  const handleBreadcrumbNavigate = useCallback((index: number) => {
    if (index === 0) {
      setActiveClusterId(null);
      setSelectedQuery(null);
      setZoomLevel(1);
      // Reset zoom
      if (svgRef.current && zoomBehaviorRef.current) {
        d3.select(svgRef.current)
          .transition()
          .duration(500)
          .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
      }
    }
  }, []);

  // ── Click handlers ───────────────────────────────────────────────────

  const handleClusterClick = useCallback((cluster: TerrainCluster) => {
    setActiveClusterId(cluster.id);
    setSelectedQuery(null);
    setZoomLevel(4); // Jump to level 2
  }, []);

  const handleQueryClick = useCallback((query: NeighbourhoodQuery) => {
    setSelectedQuery(query);
  }, []);

  const handleBackToTerrain = useCallback(() => {
    setActiveClusterId(null);
    setSelectedQuery(null);
    setZoomLevel(1);
    if (svgRef.current && zoomBehaviorRef.current) {
      d3.select(svgRef.current)
        .transition()
        .duration(500)
        .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    }
  }, []);

  // ── Right panel data ─────────────────────────────────────────────────

  const counterItems = useMemo(() => {
    if (activeClusterId && neighbourhoodData) {
      return neighbourhoodData.source_breakdown.map(s => ({
        label: getSourceConfig(s.source_type).label,
        count: s.count,
        color: getSourceConfig(s.source_type).stroke,
      }));
    }
    // Level 1: cluster count by dominant source
    const sourceMap: Record<string, number> = {};
    for (const c of clusters) {
      const src = c.dominant_source || 'other';
      sourceMap[src] = (sourceMap[src] || 0) + 1;
    }
    return Object.entries(sourceMap).map(([src, count]) => ({
      label: getSourceConfig(src).label,
      count,
      color: getSourceConfig(src).stroke,
    }));
  }, [clusters, activeClusterId, neighbourhoodData]);

  const contextSummary = useMemo(() => {
    if (activeClusterId && neighbourhoodData) {
      const unanswered = neighbourhoodData.queries.filter(q => !q.answered).length;
      return {
        totalQueries: neighbourhoodData.total_queries,
        unanswered,
        value: num(neighbourhoodData.cluster.total_commercial_value_eur),
      };
    }
    const totalQueries = clusters.reduce((s, c) => s + num(c.member_count), 0);
    const totalValue = clusters.reduce((s, c) => s + num(c.total_commercial_value_eur), 0);
    return { totalQueries, unanswered: 0, value: totalValue };
  }, [clusters, activeClusterId, neighbourhoodData]);

  // ── D3 rendering ─────────────────────────────────────────────────────

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const width = svg.clientWidth;
    const height = svg.clientHeight;
    if (width === 0 || height === 0) return;

    const svgSel = d3.select(svg);
    svgSel.selectAll('*').remove();

    // Create root group for zoom
    const g = svgSel.append('g');
    gRef.current = g.node();

    // Tooltip div
    const tooltip = d3.select(tooltipRef.current);

    // Zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 30])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        setZoomLevel(event.transform.k);
      });

    zoomBehaviorRef.current = zoom;
    svgSel.call(zoom);

    if (!activeClusterId) {
      // ── LEVEL 1: Cluster bubbles ──────────────────────────────────
      if (clusters.length === 0) return;

      const maxMember = Math.max(...clusters.map(c => num(c.member_count)), 1);

      // Scale positions to canvas
      const xExtent = d3.extent(clusters, d => num(d.umap_x)) as [number, number];
      const yExtent = d3.extent(clusters, d => num(d.umap_y)) as [number, number];

      const xScale = d3.scaleLinear()
        .domain([xExtent[0] - 0.2, xExtent[1] + 0.2])
        .range([100, width - 100]);
      const yScale = d3.scaleLinear()
        .domain([yExtent[0] - 0.2, yExtent[1] + 0.2])
        .range([80, height - 80]);

      const radiusFn = (count: number) => {
        const ratio = count / maxMember;
        return Math.max(12, Math.min(80, Math.sqrt(ratio) * 80));
      };

      // Filter by source
      const filtered = activeSource
        ? clusters.filter(c => c.dominant_source === activeSource)
        : clusters;
      const filteredIds = new Set(filtered.map(c => c.id));

      // Filter by search
      const matchesSearch = (c: TerrainCluster) => {
        if (!debouncedSearch) return true;
        return (c.label || '').toLowerCase().includes(debouncedSearch.toLowerCase());
      };

      // Draw all clusters (dimmed if filtered out)
      const clusterGroups = g.selectAll<SVGGElement, TerrainCluster>('.cluster')
        .data(clusters, d => d.id)
        .join('g')
        .attr('class', 'cluster')
        .attr('transform', d => `translate(${xScale(num(d.umap_x))},${yScale(num(d.umap_y))})`)
        .style('cursor', 'pointer')
        .style('opacity', d => {
          const sourceMatch = !activeSource || filteredIds.has(d.id);
          const searchMatch = matchesSearch(d);
          return sourceMatch && searchMatch ? 1 : 0.08;
        });

      clusterGroups.append('circle')
        .attr('r', d => radiusFn(num(d.member_count)))
        .attr('fill', d => getSourceConfig(d.dominant_source).fill)
        .attr('stroke', d => getSourceConfig(d.dominant_source).stroke)
        .attr('stroke-width', 2);

      // Trending indicator
      clusterGroups.filter(d => d.trending)
        .append('circle')
        .attr('r', d => radiusFn(num(d.member_count)) + 4)
        .attr('fill', 'none')
        .attr('stroke', d => getSourceConfig(d.dominant_source).stroke)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,3')
        .attr('opacity', 0.5);

      // Labels inside for large bubbles
      clusterGroups.filter(d => radiusFn(num(d.member_count)) >= 30)
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '-0.2em')
        .attr('fill', d => getSourceConfig(d.dominant_source).stroke)
        .attr('font-size', d => Math.min(13, radiusFn(num(d.member_count)) / 4))
        .attr('font-weight', 600)
        .text(d => {
          const label = d.label || '';
          const r = radiusFn(num(d.member_count));
          const maxChars = Math.floor(r / 4);
          return label.length > maxChars ? label.slice(0, maxChars - 1) + '\u2026' : label;
        });

      clusterGroups.filter(d => radiusFn(num(d.member_count)) >= 30)
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '1.1em')
        .attr('fill', d => getSourceConfig(d.dominant_source).stroke)
        .attr('font-size', 10)
        .attr('opacity', 0.7)
        .text(d => `${fmt(num(d.member_count))} queries`);

      // Interaction
      clusterGroups
        .on('mouseover', function (event, d) {
          d3.select(this).select('circle').attr('stroke-width', 3);
          tooltip
            .style('display', 'block')
            .style('left', `${event.pageX + 12}px`)
            .style('top', `${event.pageY - 20}px`)
            .html(`
              <div class="font-semibold text-sm">${d.label || 'Unlabelled cluster'}</div>
              <div class="text-xs text-muted-foreground mt-1">
                ${fmt(num(d.member_count))} queries &middot; ${fmtEur(num(d.total_commercial_value_eur))} EUR
              </div>
              <div class="text-xs mt-0.5">${getSourceConfig(d.dominant_source).label}</div>
            `);
        })
        .on('mousemove', function (event) {
          tooltip
            .style('left', `${event.pageX + 12}px`)
            .style('top', `${event.pageY - 20}px`);
        })
        .on('mouseout', function () {
          d3.select(this).select('circle').attr('stroke-width', 2);
          tooltip.style('display', 'none');
        })
        .on('click', function (event, d) {
          event.stopPropagation();
          handleClusterClick(d);
        });

    } else if (neighbourhoodData) {
      // ── LEVEL 2/3: Query dots / text ──────────────────────────────
      const queries = neighbourhoodData.queries;
      const subClusters = neighbourhoodData.sub_clusters;
      if (queries.length === 0 && subClusters.length === 0) return;

      // Scale query positions
      const allX = queries.map(q => num(q.x));
      const allY = queries.map(q => num(q.y));
      const xExtent = d3.extent(allX) as [number, number];
      const yExtent = d3.extent(allY) as [number, number];

      const pad = 0.15;
      const xScale = d3.scaleLinear()
        .domain([
          (xExtent[0] || -1) - pad,
          (xExtent[1] || 1) + pad,
        ])
        .range([60, width - 60]);
      const yScale = d3.scaleLinear()
        .domain([
          (yExtent[0] || -1) - pad,
          (yExtent[1] || 1) + pad,
        ])
        .range([60, height - 60]);

      const maxFreq = Math.max(...queries.map(q => num(q.frequency)), 1);

      // Filter
      const matchesSource = (q: NeighbourhoodQuery) => !activeSource || q.source_type === activeSource;
      const matchesSearchQ = (q: NeighbourhoodQuery) => {
        if (!debouncedSearch) return true;
        const t = q.text || q.text_original || '';
        return t.toLowerCase().includes(debouncedSearch.toLowerCase());
      };

      // Draw sub-clusters as larger translucent circles
      if (subClusters.length > 0) {
        const scXScale = d3.scaleLinear()
          .domain([-1.2, 1.2])
          .range([80, width - 80]);
        const scYScale = d3.scaleLinear()
          .domain([-1.2, 1.2])
          .range([80, height - 80]);

        const scGroups = g.selectAll<SVGGElement, SubCluster>('.sub-cluster')
          .data(subClusters, d => d.id)
          .join('g')
          .attr('class', 'sub-cluster')
          .attr('transform', d => `translate(${scXScale(num(d.umap_x))},${scYScale(num(d.umap_y))})`)
          .style('cursor', 'pointer');

        scGroups.append('circle')
          .attr('r', d => Math.max(20, Math.min(50, Math.sqrt(num(d.member_count) / 10) * 10)))
          .attr('fill', 'rgba(148,163,184,0.12)')
          .attr('stroke', '#94A3B8')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,2');

        scGroups.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .attr('fill', '#64748B')
          .attr('font-size', 10)
          .attr('font-weight', 500)
          .text(d => {
            const maxC = 18;
            return d.label.length > maxC ? d.label.slice(0, maxC - 1) + '\u2026' : d.label;
          });

        scGroups
          .on('click', function (event, d) {
            event.stopPropagation();
            setActiveClusterId(d.id);
            setSelectedQuery(null);
          });
      }

      // Determine if we should show text (high zoom) or dots
      const showText = zoomLevel > 12;

      if (showText) {
        // ── LEVEL 3: Text labels ──────────────────────────────────
        const textGroups = g.selectAll<SVGGElement, NeighbourhoodQuery>('.query-text')
          .data(queries, d => d.id)
          .join('g')
          .attr('class', 'query-text')
          .attr('transform', d => `translate(${xScale(num(d.x))},${yScale(num(d.y))})`)
          .style('cursor', 'pointer')
          .style('opacity', d => matchesSource(d) && matchesSearchQ(d) ? 1 : 0.08);

        textGroups.append('text')
          .attr('text-anchor', 'start')
          .attr('dx', 4)
          .attr('dy', '0.35em')
          .attr('fill', d => getSourceConfig(d.source_type).stroke)
          .attr('font-size', d => {
            const ratio = num(d.frequency) / maxFreq;
            return Math.max(9, Math.min(18, 9 + ratio * 9));
          })
          .attr('font-weight', d => num(d.frequency) > maxFreq * 0.5 ? 600 : 400)
          .text(d => d.text || d.text_original || '');

        // Small dot before text
        textGroups.append('circle')
          .attr('r', 2)
          .attr('fill', d => getSourceConfig(d.source_type).stroke);

        textGroups
          .on('click', function (event, d) {
            event.stopPropagation();
            handleQueryClick(d);
          });

      } else {
        // ── LEVEL 2: Query dots ─────────────────────────────────────
        const dotGroups = g.selectAll<SVGGElement, NeighbourhoodQuery>('.query-dot')
          .data(queries, d => d.id)
          .join('g')
          .attr('class', 'query-dot')
          .attr('transform', d => `translate(${xScale(num(d.x))},${yScale(num(d.y))})`)
          .style('cursor', 'pointer')
          .style('opacity', d => matchesSource(d) && matchesSearchQ(d) ? 1 : 0.08);

        dotGroups.append('circle')
          .attr('r', d => {
            const ratio = num(d.frequency) / maxFreq;
            return Math.max(2, Math.min(6, 2 + ratio * 4));
          })
          .attr('fill', d => getSourceConfig(d.source_type).fill)
          .attr('stroke', d => getSourceConfig(d.source_type).stroke)
          .attr('stroke-width', 1);

        // Interaction
        dotGroups
          .on('mouseover', function (event, d) {
            d3.select(this).select('circle').attr('stroke-width', 2).attr('r', function () {
              return parseFloat(d3.select(this).attr('r')) + 2;
            });
            tooltip
              .style('display', 'block')
              .style('left', `${event.pageX + 12}px`)
              .style('top', `${event.pageY - 20}px`)
              .html(`
                <div class="font-medium text-xs">${d.text || d.text_original || ''}</div>
                <div class="text-xs text-muted-foreground mt-0.5">
                  Vol: ${fmt(num(d.frequency))} &middot; ${fmtEur(num(d.commercial_value))} EUR
                </div>
              `);
          })
          .on('mousemove', function (event) {
            tooltip
              .style('left', `${event.pageX + 12}px`)
              .style('top', `${event.pageY - 20}px`);
          })
          .on('mouseout', function (event, d) {
            const ratio = num(d.frequency) / maxFreq;
            const baseR = Math.max(2, Math.min(6, 2 + ratio * 4));
            d3.select(this).select('circle').attr('stroke-width', 1).attr('r', baseR);
            tooltip.style('display', 'none');
          })
          .on('click', function (event, d) {
            event.stopPropagation();
            handleQueryClick(d);
          });
      }
    }

    // Click on background to deselect
    svgSel.on('click', () => {
      setSelectedQuery(null);
    });

  }, [
    clusters, activeClusterId, neighbourhoodData, activeSource,
    debouncedSearch, zoomLevel, handleClusterClick, handleQueryClick,
  ]);

  // ── Loading state ──────────────────────────────────────────────────

  const isLoading = terrainLoading || (activeClusterId && neighbourhoodLoading);

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background/95 backdrop-blur-sm h-12 shrink-0">
        <div className="flex items-center gap-3">
          {activeClusterId && (
            <button
              onClick={handleBackToTerrain}
              className="p-1 rounded hover:bg-muted transition-colors"
              title="Back to all clusters"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <Breadcrumb items={breadcrumbItems} onNavigate={handleBreadcrumbNavigate} />
        </div>
        <div className="flex items-center gap-3">
          <SourceFilterPills activeSource={activeSource} onSelect={setActiveSource} />
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="pl-7 h-7 w-40 text-xs"
            />
          </div>
          {/* Market selector */}
          {markets.length > 1 && (
            <select
              value={activeMarket || ''}
              onChange={e => {
                setActiveMarket(e.target.value);
                setActiveClusterId(null);
                setSelectedQuery(null);
              }}
              className="h-7 text-xs rounded border bg-background px-2"
            >
              {markets.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 min-h-0">
        {/* SVG canvas (65%) */}
        <div className="flex-1 relative" style={{ minWidth: 0 }}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Loading...
              </div>
            </div>
          )}

          {!siteId && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Globe2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Select a site to explore demand terrain</p>
              </div>
            </div>
          )}

          {siteId && !isLoading && clusters.length === 0 && !activeClusterId && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No clusters found</p>
                <p className="text-xs mt-1">Run the synthesis pipeline first to generate clusters</p>
              </div>
            </div>
          )}

          <svg
            ref={svgRef}
            className="w-full h-full"
            style={{ background: 'transparent' }}
          />

          {/* Tooltip */}
          <div
            ref={tooltipRef}
            className="fixed z-50 pointer-events-none px-3 py-2 rounded-lg shadow-lg border bg-popover text-popover-foreground"
            style={{ display: 'none' }}
          />

          {/* Zoom indicator */}
          <div className="absolute bottom-3 left-3 text-xs text-muted-foreground bg-background/80 backdrop-blur-sm px-2 py-1 rounded border">
            <Eye className="w-3 h-3 inline mr-1" />
            {zoomLevel < 3 ? 'L1 Clusters' : zoomLevel < 12 ? 'L2 Queries' : 'L3 Detail'}
            {' '}&middot; {zoomLevel.toFixed(1)}x
          </div>
        </div>

        {/* Right panel (35%, 280px min) */}
        <div className="w-[280px] shrink-0 border-l bg-background overflow-y-auto">
          <div className="p-3 space-y-4">
            {/* Zone A: Counters */}
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {activeClusterId ? 'Sources' : 'Clusters by source'}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <CounterList items={counterItems} />
              </CardContent>
            </Card>

            {/* Zone B: Selected item */}
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {activeClusterId ? 'Selected query' : 'Selected cluster'}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                {activeClusterId ? (
                  <QueryDetailCard query={selectedQuery} />
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    Click a cluster to explore
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Zone C: Context summary */}
            <Card>
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-3">
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total queries</span>
                    <span className="font-semibold tabular-nums">{fmt(contextSummary.totalQueries)}</span>
                  </div>
                  {activeClusterId && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Unanswered</span>
                      <span className="font-semibold tabular-nums text-amber-600">{fmt(contextSummary.unanswered)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Commercial value</span>
                    <span className="font-semibold tabular-nums">{fmtEur(contextSummary.value)} EUR</span>
                  </div>
                  {!activeClusterId && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Clusters</span>
                      <span className="font-semibold tabular-nums">{clusters.length}</span>
                    </div>
                  )}
                  {activeClusterId && neighbourhoodData && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Gap score</span>
                        <span className="font-semibold tabular-nums">
                          {num(neighbourhoodData.cluster.gap_score).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Coverage</span>
                        <span className="font-semibold tabular-nums">
                          {num(neighbourhoodData.cluster.coverage_pct).toFixed(0)}%
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Market selector (compact, in panel) */}
            {markets.length > 1 && (
              <Card>
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Market
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="flex flex-wrap gap-1">
                    {markets.map(m => (
                      <button
                        key={m}
                        onClick={() => {
                          setActiveMarket(m);
                          setActiveClusterId(null);
                          setSelectedQuery(null);
                        }}
                        className={`px-2 py-0.5 rounded text-xs transition-colors ${
                          activeMarket === m
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
